import {ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit, ViewEncapsulation} from '@angular/core';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {ClientStatus, R2CloudClientStatus} from '../models';
import {faCircle, faExclamationTriangle, faLaptopCode} from '@fortawesome/free-solid-svg-icons';
import {R2CloudClientService} from '../ws/r2-cloud-client.service';
import {ComponentCleaner} from '../component-cleaner';
import {fuseAnimations} from '../../../@fuse/animations';
import {ClientDownloadService} from '../main/desktop/client-download.service';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {ConfirmDialogComponent} from '../helpers/confirm-dialog/confirm-dialog.component';
import {ValueWrapper} from '../helpers/value-wrapper';
import {R2CloudStompService} from '../ws/r2-cloud-stomp.service';
import {R2CloudHttpApiService} from '../r2-cloud-http-api.service';
import {debounce, map, startWith} from 'rxjs/operators';
import {combineLatest, fromEvent, timer} from 'rxjs';
import {IconDefinition} from '@fortawesome/fontawesome-common-types';
import {ContingencyService} from '../main/login/contingency.service';
import {LocalClientService} from '../main/login/local-client.service';
import {decideStompAlert, StompAlertOutcome, stompAlertDeadlineMs, stompAlertText} from '../stomp/stomp-alert-rules';

@Component({
    selector: 'app-client-status-toolbar',
    templateUrl: './client-status-toolbar.component.html',
    styleUrls: ['./client-status-toolbar.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ClientStatusToolbarComponent extends ComponentCleaner implements OnInit {
    private static readonly STOMP_RETRY_COUNT_KEY = 'r2cloud-stomp-retry-count';
    private readonly destroyRef = inject(DestroyRef);
    private readonly cdr = inject(ChangeDetectorRef);
    faCircle = faCircle;
    clientStatus!: ClientStatus;
    R2CloudClientStatus = R2CloudClientStatus;
    connectedOnce = false;
    showErrorMessage = false;
    /**
     * Prazo do alerta do AGENTE / cliente nativo. **Não vale mais para o STOMP** (R2CLOUD-1169): o
     * prazo do STOMP vem de `stompAlertDeadlineMs()`, porque 15 s é menos que o tempo que o próprio
     * portal leva para subir a sessão STOMP numa carga limpa (21-26 s medidos).
     */
    timeUntilErrorMessageAppears = 15000;
    modalAlertError: MatDialogRef<any> | null = null;
    /** R2CLOUD-1169: o STOMP já subiu nesta carga da página? Muda o prazo (carga fria vs. reconexão). */
    private stompConnectedAtLeastOnce = false;
    /**
     * Desde quando o portal está sem STOMP, em `Date.now()`; `null` quando o STOMP está de pé.
     *
     * Começa preenchido porque a carga da página começa SEM STOMP — o `R2CloudClientService` nasce
     * com `STOMP_NOT_CONNECTED` —, então o decorrido conta desde aqui e inclui o que a SPA gasta
     * antes da primeira tentativa, que é como o orçamento de 21-26 s foi medido.
     */
    private stompDownSince: number | null = Date.now();
    private readonly isClientOpenNow = toSignal(this.localClientService.isClientOpen$(), {initialValue: false});
    contingencyEnabled = toSignal(this.contingencyService.isEnabled$());

    constructor(
        private clientService: R2CloudClientService,
        private clientDownload: ClientDownloadService,
        private stomp: R2CloudStompService,
        private dialog: MatDialog,
        private http: R2CloudHttpApiService,
        public contingencyService: ContingencyService,
        public localClientService: LocalClientService,
    ) {
        super();
        this.delayedErrorMessage();

        this.clientService.clientConnectionChanges().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((status) => {
            this.onClientStatus(status);
            this.cdr.markForCheck();
        });
        this.clientService.startClientListener();

        if (this.contingencyService.isEnabled()) {
            setTimeout(() => {
                if (!this.isClientOpenNow()) {
                    this.localClientService.start();
                }
            }, 2000);

            this.addTimeout(setInterval(() => {
                if (!this.isClientOpenNow()) {
                    this.localClientService.start();
                    this.localClientService.status$({reason: 'status'}).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
                }
            }, 30000));
        }

    }

    ngOnInit(): void {

    }

    private delayedErrorMessage(): void {
        const visibilityChange$ = fromEvent(document, 'visibilitychange')
            .pipe(startWith({} as Event), map(() => document.hidden),
                debounce((x: boolean) => {
                    if (x) {
                        return timer(0);
                    } else {
                        return timer(this.timeUntilErrorMessageAppears);
                    }
                }));
        const wrapper$ = this.clientService.clientConnectionChanges().pipe(
            map((v) => v.status),
            map((status) => ({
                    isError: status !== R2CloudClientStatus.CONNECTED && status !== R2CloudClientStatus.CLIENT_UPDATE,
                    status: status
                } as ClientStatusWrapper)),
            debounce((v) => {
                if (!v.isError) {
                    return timer(0);
                }
                if (v.status === R2CloudClientStatus.STOMP_NOT_CONNECTED) {
                    // R2CLOUD-1169: só o ramo do STOMP muda de prazo. O do agente continua em
                    // `timeUntilErrorMessageAppears` — outro dono, outra causa, outro orçamento.
                    return timer(stompAlertDeadlineMs(this.stompConnectedAtLeastOnce));
                }
                return timer(this.timeUntilErrorMessageAppears);
            }));
        combineLatest([
            wrapper$,
            visibilityChange$,
        ])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((status) => {
                const hidden = status[1];
                const wrapper = status[0];
                this.showErrorMessage = !hidden && wrapper.isError;
                if (this.showErrorMessage) {
                    this.alertError(wrapper.status);
                } else {
                    this.closeAlertError();
                }
                this.cdr.markForCheck();
            });
    }

    private onClientStatus(result: ClientStatus): void {
        const clientStatus = result;
        // R2CLOUD-1169: antes de qualquer `return` deste método — os dois abaixo saem cedo, e o
        // relógio do STOMP não pode depender de qual ramo o status do agente tomou.
        this.trackStompAvailability(clientStatus.status);
        if (clientStatus.status === R2CloudClientStatus.CONNECTED) {
            if (clientStatus.webClientId !== this.stomp.webClientId) {
                this.http.post<ValueWrapper>(`manager-users/disconnect-windows-client/${clientStatus.clientId}`, null).subscribe(() => {
                    },
                    (error) => {
                        console.error(error);
                    });
            }
            this.connectedOnce = true;
            this.resetStompRetryCount();
        }
        if (this.clientStatus && this.clientStatus.status === R2CloudClientStatus.CLIENT_UPDATE && clientStatus.status === R2CloudClientStatus.NOT_CONNECTED) {
            return;
        }
        if (this.connectedOnce && this.clientStatus && clientStatus.status === R2CloudClientStatus.NOT_INSTALLED) {
            this.clientStatus.status = R2CloudClientStatus.NOT_CONNECTED;
        } else {
            this.clientStatus = clientStatus;
        }
    }


    downloadLatestVersion(): void {
        this.clientDownload.download();
    }

    /**
     * Guarda o que a regra do R2CLOUD-1169 precisa saber: se o STOMP já subiu nesta carga da página
     * e desde quando ele está fora.
     *
     * Qualquer status diferente de `STOMP_NOT_CONNECTED` significa STOMP de pé — é o próprio
     * `R2CloudClientService` que devolve `STOMP_NOT_CONNECTED` para TODO status enquanto a conexão
     * não está `OK`, então não há status "sem STOMP" fora desse.
     *
     * O relógio só é iniciado na primeira observação da queda: reiniciá-lo a cada emissão repetida
     * faria o decorrido nunca crescer e o alerta nunca aparecer, nem com o servidor realmente fora.
     */
    private trackStompAvailability(status: R2CloudClientStatus): void {
        if (status === R2CloudClientStatus.STOMP_NOT_CONNECTED) {
            if (this.stompDownSince === null) {
                this.stompDownSince = Date.now();
            }
            return;
        }
        this.stompConnectedAtLeastOnce = true;
        this.stompDownSince = null;
    }

    /** Desfecho do alerta de STOMP agora, a partir do que `trackStompAvailability` vem observando. */
    private currentStompAlertOutcome(): StompAlertOutcome {
        const stompConnected = this.stompDownSince === null;
        const millisecondsWithoutStomp = this.stompDownSince === null ? 0 : Date.now() - this.stompDownSince;
        return decideStompAlert(stompConnected, this.stompConnectedAtLeastOnce, millisecondsWithoutStomp);
    }

    private ignoreAlertOnThesePaths(): boolean {
        return window.location.pathname === '/profile';
    }

    alertError(status: R2CloudClientStatus): void {
        if (!this.modalAlertError) {
            if (this.ignoreAlertOnThesePaths()) {
                return;
            }
            if (this.contingencyService.isEnabled()) {
                return;
            }
            let message: string;
            let buttonValue: string;
            let cancelButtonValue: string | null = null;
            if (status === R2CloudClientStatus.STOMP_NOT_CONNECTED) {
                const outcome = this.currentStompAlertOutcome();
                if (outcome !== 'server-unreachable') {
                    // R2CLOUD-1169: o portal ainda está dentro do próprio orçamento de recuperação do
                    // STOMP (21-26 s medidos). Bloquear a tela aqui é o defeito, não a defesa — e o
                    // botão do diálogo recarrega a página, o que reiniciaria a corrida do zero.
                    return;
                }
                const text = stompAlertText(outcome);
                message = text.message;
                buttonValue = text.confirmButton;
                if (this.getStompRetryCount() >= 3) {
                    cancelButtonValue = 'Modo contingência';
                }
            } else {
                if (this.connectedOnce) {
                    message = 'Para reativar a tela clique no botão abaixo.';
                    buttonValue = 'Reativar';
                } else {
                    message = 'Agente não detectado.\nÉ possivel tentar abri-lo novamente ou abra o instalador clicando em Download.';
                    cancelButtonValue = 'Tentar Abrir';
                    buttonValue = 'Download';
                    if (this.ignoreAlertOnThesePaths()) {
                        return;
                    }
                }
            }
            this.modalAlertError = this.dialog.open(ConfirmDialogComponent, {
                disableClose: true,
                data: {
                    message: message,
                    disableCancel: cancelButtonValue === null,
                    confirmButtonValue: buttonValue,
                    cancelButtonValue: cancelButtonValue,
                    icon: 'info_outline'
                }
            });
            this.modalAlertError?.afterClosed().subscribe((result) => {
                this.modalAlertError = null;
                const isCancel = !result;
                const isContingencyMode = cancelButtonValue?.toLowerCase().includes('modo') ?? false;

                if (isCancel && isContingencyMode) {
                    this.contingencyService.toggleContingencyMode(
                        !this.contingencyService.isEnabled(),
                        () => this.resetStompRetryCount()
                    );
                    this.cdr.markForCheck();
                    return;
                }
                if (status === R2CloudClientStatus.STOMP_NOT_CONNECTED) {
                    this.incrementStompRetryCount();
                    window.location.reload();
                    return;
                }
                if (isCancel && (status === R2CloudClientStatus.INSTALLED || status === R2CloudClientStatus.NOT_INSTALLED)) {
                    this.clientService.startClientClick({ isTrusted: true } as Event);
                    this.cdr.markForCheck();
                    return;
                }
                if (!this.connectedOnce && result) {
                    this.downloadLatestVersion();
                }
                this.cdr.markForCheck();
            });

        }
    }

    private getStompRetryCount(): number {
        return parseInt(localStorage.getItem(ClientStatusToolbarComponent.STOMP_RETRY_COUNT_KEY) || '0', 10);
    }

    private incrementStompRetryCount(): void {
        localStorage.setItem(ClientStatusToolbarComponent.STOMP_RETRY_COUNT_KEY, String(this.getStompRetryCount() + 1));
    }

    private resetStompRetryCount(): void {
        localStorage.removeItem(ClientStatusToolbarComponent.STOMP_RETRY_COUNT_KEY);
    }

    closeAlertError(): void {
        this.modalAlertError?.close();
        this.modalAlertError = null;
    }

    get agentIconClass(): string {
        if (!this.clientStatus || !this.clientStatus.status) {
            return '';
        }
        switch (this.clientStatus.status) {
            case R2CloudClientStatus.CONNECTED:
                return 'client-connected';
            case R2CloudClientStatus.CLIENT_UPDATE:
                return 'client-update';
            case R2CloudClientStatus.NOT_CONNECTED:
                return 'client-not-connected';
            case R2CloudClientStatus.INSTALLED:
                return 'client-installed';
            case R2CloudClientStatus.NOT_INSTALLED:
                return 'client-not-installed';
            case R2CloudClientStatus.STOMP_NOT_CONNECTED:
                return 'client-stomp-not-connected';
            case R2CloudClientStatus.OPENING:
                return 'client-opening';
        }
    }

    get agentIcon(): IconDefinition {
        if (!this.clientStatus || !this.clientStatus.status) {
            return faCircle;
        }
        switch (this.clientStatus.status) {
            case R2CloudClientStatus.CONNECTED:
                return faCircle;
            case R2CloudClientStatus.CLIENT_UPDATE:
                return faLaptopCode;
            case R2CloudClientStatus.NOT_CONNECTED:
                return faLaptopCode;
            case R2CloudClientStatus.INSTALLED:
                return faCircle;
            case R2CloudClientStatus.NOT_INSTALLED:
                return faLaptopCode;
            case R2CloudClientStatus.STOMP_NOT_CONNECTED:
                return faExclamationTriangle;
            case R2CloudClientStatus.OPENING:
                return faLaptopCode;
        }
    }

    get agentTooltip(): string {
        if (!this.clientStatus || !this.clientStatus.status) {
            return '';
        }
        switch (this.clientStatus.status) {
            case R2CloudClientStatus.CONNECTED:
                return 'Agente conectado';
            case R2CloudClientStatus.CLIENT_UPDATE:
                return 'Agente sendo atualizado';
            case R2CloudClientStatus.NOT_CONNECTED:
                return 'Agente desconectado';
            case R2CloudClientStatus.INSTALLED:
                return 'Agente instalado';
            case R2CloudClientStatus.NOT_INSTALLED:
                return 'Agente não instalado. (Clique para fazer o Download)';
            case R2CloudClientStatus.STOMP_NOT_CONNECTED:
                return 'Servidor desconectado';
            case R2CloudClientStatus.OPENING:
                return 'Agente abrindo';
        }
    }

    get agentStatusCursor(): string {
        if (!this.clientStatus || !this.clientStatus.status) {
            return 'default';
        }
        switch (this.clientStatus.status) {
            case R2CloudClientStatus.CONNECTED:
                return 'default';
            case R2CloudClientStatus.CLIENT_UPDATE:
                return 'default';
            case R2CloudClientStatus.NOT_CONNECTED:
                return 'pointer';
            case R2CloudClientStatus.INSTALLED:
                return 'pointer';
            case R2CloudClientStatus.NOT_INSTALLED:
                return 'pointer';
            case R2CloudClientStatus.STOMP_NOT_CONNECTED:
                return 'pointer';
            case R2CloudClientStatus.OPENING:
                return 'pointer';
        }
    }

    agentStatusClick(e: Event): void {
        if (!this.clientStatus || !this.clientStatus.status) {
            return;
        }
        switch (this.clientStatus.status) {
            case R2CloudClientStatus.CONNECTED:
                return;
            case R2CloudClientStatus.CLIENT_UPDATE:
                return;
            case R2CloudClientStatus.NOT_CONNECTED:
                return this.clientService.startClientClick(e);
            case R2CloudClientStatus.INSTALLED:
                return this.clientService.startClientClick(e);
            case R2CloudClientStatus.NOT_INSTALLED:
                return this.downloadLatestVersion();
            case R2CloudClientStatus.STOMP_NOT_CONNECTED:
                return window.location.reload();
            case R2CloudClientStatus.OPENING:
                return this.clientService.startClientClick(e);
        }
    }

    public openClient(_: Event): void {
        this.localClientService.start();
    }
}

interface ClientStatusWrapper {
    isError: boolean;
    status: R2CloudClientStatus;
}
